/**
 * Cache offline básico en localStorage para catálogo (productos + clientes).
 *
 * Se usa como fallback cuando el vendedor abre la app sin conexión.
 * Guarda solo los últimos N registros (livianos) y respeta un TTL para no
 * mostrar datos rancios.
 */

const KEYS = {
  // Feb/2026: bump a v3 → añade `descripcion` al slim.
  productos: "beem_cache_productos_v3",
  clientes: "beem_cache_clientes_v3",
};

const LIMITS = {
  productos: 100,
  clientes: 50,
};

const TTL_MS = 24 * 60 * 60 * 1000; // 24h

function _read(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.at || !Array.isArray(parsed.items)) return null;
    if (Date.now() - parsed.at > TTL_MS) return null;
    return parsed;
  } catch { return null; }
}

function _write(key, items) {
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), items }));
  } catch (e) {
    // localStorage lleno o modo privado → fail-silent
  }
}

/** Guarda hasta N items del catálogo, quedándose con los primeros (asumimos que la API ya los ordena por relevancia). */
export function cacheCatalog(kind, items) {
  if (!KEYS[kind] || !Array.isArray(items)) return;
  const limit = LIMITS[kind];
  // Recortamos campos pesados para no exceder localStorage (~5MB)
  const slim = items.slice(0, limit).map((x) => ({
    id: x.id,
    nombre: x.nombre,
    sku: x.sku,
    // Feb/2026: codigo (CLIENT'S REF / EF26-XX) — crítico para operadores
    // que buscan productos en bodega. Si se omite del caché, aparece vacío
    // en la UI al releer offline.
    codigo: x.codigo,
    descripcion: x.descripcion,
    familia_key: x.familia_key,
    color: x.color,
    imagen_path: x.imagen_path,
    imagen_actualizada_at: x.imagen_actualizada_at,
    precio_sugerido: x.precio_sugerido,
    precio_minimo: x.precio_minimo,
    stock_actual: x.stock_actual,
    stock_reservado: x.stock_reservado,
    stock_minimo: x.stock_minimo,
    unidad: x.unidad,
    categoria: x.categoria,
    categoria_nombre: x.categoria_nombre,
    es_padre: x.es_padre,
    variantes_count: x.variantes_count,
    activo: x.activo,
    archivado: x.archivado,
    // clientes
    telefono: x.telefono,
    ciudad: x.ciudad,
    segmento: x.segmento,
    lista_precios_id: x.lista_precios_id,
    vendedor_asignado_id: x.vendedor_asignado_id,
    total_compras: x.total_compras,
    saldo_pendiente: x.saldo_pendiente,
  }));
  _write(KEYS[kind], slim);
}

/** Devuelve el cache si existe y no venció, o null. */
export function getCachedCatalog(kind) {
  if (!KEYS[kind]) return null;
  const parsed = _read(KEYS[kind]);
  return parsed ? parsed.items : null;
}

/** Metadata para mostrar al vendedor cuando cargamos desde cache. */
export function getCatalogCacheMeta(kind) {
  if (!KEYS[kind]) return null;
  const parsed = _read(KEYS[kind]);
  if (!parsed) return null;
  return {
    at: parsed.at,
    count: parsed.items.length,
    ageMinutes: Math.round((Date.now() - parsed.at) / 60000),
  };
}

// Feb/2026: al cargar el módulo, purgar claves viejas de localStorage
// (v1) que quedaron con productos sin `codigo`. Idempotente.
try {
  ["beem_cache_productos_v1", "beem_cache_clientes_v1",
   "beem_cache_productos_v2", "beem_cache_clientes_v2"].forEach((k) => {
    try { localStorage.removeItem(k); } catch {}
  });
} catch {}
